import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { dashApi } from "@/lib/dashApi";
import { fmtDateTime, fmtRelative } from "@/lib/format";
import { EmptyState, ErrorState, LoadingState, StatusPill } from "@/components/dash/ui";

const INPUT = "rounded-md border border-ink-500/60 bg-ink-800 px-2 py-1 text-xs text-slate-200 [color-scheme:dark]";
const BTN = "rounded-md border border-ink-500/60 bg-ink-800 px-2.5 py-1 text-xs text-slate-200 hover:bg-ink-700 disabled:opacity-50";
const BTN_PRIMARY = "rounded-md border border-brand-500/40 bg-brand-500/10 px-2.5 py-1 text-xs text-brand-200 hover:bg-brand-500/20 disabled:opacity-50";

function Section({ title, desc, children }: { title: string; desc?: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3 border-t border-ink-500/40 pt-5 first:border-t-0 first:pt-0">
      <div>
        <h2 className="text-sm font-semibold text-slate-100">{title}</h2>
        {desc && <p className="text-xs text-slate-500">{desc}</p>}
      </div>
      {children}
    </section>
  );
}

function SchedulesSection() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["report-schedules"], queryFn: dashApi.reportSchedules });
  const mut = useMutation({
    mutationFn: ({ code, body }: { code: string; body: Parameters<typeof dashApi.updateSchedule>[1] }) => dashApi.updateSchedule(code, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["report-schedules"] }),
  });
  return (
    <Section title="Report schedules" desc="Daily / weekly / monthly executive reports. Disabling a schedule stops it firing; the scheduler's atomic advance is preserved.">
      {q.isLoading ? <LoadingState /> : q.isError ? <ErrorState error={q.error} /> : (
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
            <th className="pb-2 font-medium">Code</th><th className="pb-2 font-medium">Cadence</th>
            <th className="pb-2 font-medium">Hour (UTC)</th><th className="pb-2 font-medium">Next run</th>
            <th className="pb-2 font-medium">Status</th><th className="pb-2" />
          </tr></thead>
          <tbody className="divide-y divide-ink-500/40">
            {q.data?.schedules.map((s) => (
              <tr key={s.code} className="text-slate-300">
                <td className="py-2">{s.code}<div className="text-[11px] text-slate-600">{s.kind}</div></td>
                <td className="py-2">
                  <select defaultValue={s.cadence} className={INPUT}
                    onChange={(e) => mut.mutate({ code: s.code, body: { cadence: e.target.value } })}>
                    {["daily", "weekly", "monthly"].map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </td>
                <td className="py-2">
                  <input type="number" min={0} max={23} defaultValue={s.hour} className={`${INPUT} w-16`}
                    onBlur={(e) => { const h = Number(e.target.value); if (h !== s.hour) mut.mutate({ code: s.code, body: { hour: h } }); }} />
                </td>
                <td className="py-2 text-[11px] text-slate-500">{s.next_run_at ? fmtDateTime(s.next_run_at) : "—"}</td>
                <td className="py-2"><StatusPill status={s.enabled ? "ok" : "no_data"} label={s.enabled ? "Enabled" : "Disabled"} /></td>
                <td className="py-2 text-right">
                  <button type="button" className={BTN} disabled={mut.isPending}
                    onClick={() => mut.mutate({ code: s.code, body: { enabled: !s.enabled } })}>
                    {s.enabled ? "Disable" : "Enable"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Section>
  );
}

function ChannelsSection() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["notif-channels"], queryFn: dashApi.notificationChannels });
  const [name, setName] = useState("");
  const [type, setType] = useState("log");
  const [err, setErr] = useState<string | null>(null);
  const invalidate = () => qc.invalidateQueries({ queryKey: ["notif-channels"] });
  const create = useMutation({
    mutationFn: () => dashApi.createChannel({ name, type, config: {} }),
    onSuccess: () => { setName(""); setErr(null); invalidate(); },
    onError: (e) => setErr(e instanceof Error ? e.message : String(e)),
  });
  const toggle = useMutation({ mutationFn: ({ id, enabled }: { id: number; enabled: boolean }) => dashApi.updateChannel(id, { enabled }), onSuccess: invalidate });
  const del = useMutation({ mutationFn: (id: number) => dashApi.deleteChannel(id), onSuccess: invalidate });
  return (
    <Section title="Notification channels" desc="Where reports and alerts are delivered. Only implemented types (log, email, webhook) can be created.">
      <div className="flex flex-wrap items-end gap-2">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Channel name" className={`${INPUT} w-48`} />
        <select value={type} onChange={(e) => setType(e.target.value)} className={INPUT}>
          {["log", "email", "webhook"].map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <button type="button" className={BTN_PRIMARY} disabled={!name || create.isPending} onClick={() => create.mutate()}>Add channel</button>
        {err && <span className="text-xs text-rose-400">{err}</span>}
      </div>
      {q.isLoading ? <LoadingState /> : q.isError ? <ErrorState error={q.error} /> : !q.data?.channels.length ? (
        <EmptyState compact>No channels.</EmptyState>
      ) : (
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
            <th className="pb-2 font-medium">Name</th><th className="pb-2 font-medium">Type</th><th className="pb-2 font-medium">Status</th><th className="pb-2" />
          </tr></thead>
          <tbody className="divide-y divide-ink-500/40">
            {q.data.channels.map((c) => (
              <tr key={c.id} className="text-slate-300">
                <td className="py-2">{c.name}</td>
                <td className="py-2 text-slate-400">{c.type}</td>
                <td className="py-2"><StatusPill status={c.enabled ? "ok" : "no_data"} label={c.enabled ? "Enabled" : "Disabled"} /></td>
                <td className="py-2 text-right">
                  <div className="inline-flex gap-1.5">
                    <button type="button" className={BTN} onClick={() => toggle.mutate({ id: c.id, enabled: !c.enabled })}>{c.enabled ? "Disable" : "Enable"}</button>
                    <button type="button" className={BTN} onClick={() => { if (confirm(`Delete channel "${c.name}"? Its subscriptions are removed too.`)) del.mutate(c.id); }}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Section>
  );
}

function SubscriptionsSection() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["notif-subs"], queryFn: dashApi.notificationSubscriptions });
  const chans = useQuery({ queryKey: ["notif-channels"], queryFn: dashApi.notificationChannels });
  const [channelId, setChannelId] = useState<number | "">("");
  const [target, setTarget] = useState("report");
  const [reportKind, setReportKind] = useState("daily");
  const [minSev, setMinSev] = useState("critical");
  const [err, setErr] = useState<string | null>(null);
  const invalidate = () => qc.invalidateQueries({ queryKey: ["notif-subs"] });
  const create = useMutation({
    mutationFn: () => dashApi.createSubscription({
      channel_id: Number(channelId), target,
      report_kind: target === "report" ? reportKind : null,
      min_severity: target === "alert" ? minSev : null,
    }),
    onSuccess: () => { setErr(null); invalidate(); },
    onError: (e) => setErr(e instanceof Error ? e.message : String(e)),
  });
  const toggle = useMutation({ mutationFn: ({ id, enabled }: { id: number; enabled: boolean }) => dashApi.updateSubscription(id, { enabled }), onSuccess: invalidate });
  const del = useMutation({ mutationFn: (id: number) => dashApi.deleteSubscription(id), onSuccess: invalidate });
  const chanName = (id: number) => chans.data?.channels.find((c) => c.id === id)?.name ?? `#${id}`;
  return (
    <Section title="Subscriptions" desc="Which channel receives which reports or alerts.">
      <div className="flex flex-wrap items-end gap-2">
        <select value={channelId} onChange={(e) => setChannelId(e.target.value ? Number(e.target.value) : "")} className={INPUT}>
          <option value="">Channel…</option>
          {chans.data?.channels.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={target} onChange={(e) => setTarget(e.target.value)} className={INPUT}>
          <option value="report">report</option><option value="alert">alert</option>
        </select>
        {target === "report" ? (
          <select value={reportKind} onChange={(e) => setReportKind(e.target.value)} className={INPUT}>
            {["daily", "weekly", "monthly"].map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
        ) : (
          <select value={minSev} onChange={(e) => setMinSev(e.target.value)} className={INPUT}>
            {["info", "warning", "critical"].map((s) => <option key={s} value={s}>{s}+</option>)}
          </select>
        )}
        <button type="button" className={BTN_PRIMARY} disabled={!channelId || create.isPending} onClick={() => create.mutate()}>Add subscription</button>
        {err && <span className="text-xs text-rose-400">{err}</span>}
      </div>
      {q.isLoading ? <LoadingState /> : q.isError ? <ErrorState error={q.error} /> : !q.data?.subscriptions.length ? (
        <EmptyState compact>No subscriptions.</EmptyState>
      ) : (
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
            <th className="pb-2 font-medium">Channel</th><th className="pb-2 font-medium">Target</th><th className="pb-2 font-medium">Scope</th><th className="pb-2 font-medium">Status</th><th className="pb-2" />
          </tr></thead>
          <tbody className="divide-y divide-ink-500/40">
            {q.data.subscriptions.map((s) => (
              <tr key={s.id} className="text-slate-300">
                <td className="py-2">{chanName(s.channel_id)}</td>
                <td className="py-2 text-slate-400">{s.target}</td>
                <td className="py-2 text-slate-400">{s.target === "report" ? (s.report_kind ?? "—") : `${s.min_severity ?? "info"}+`}</td>
                <td className="py-2"><StatusPill status={s.enabled ? "ok" : "no_data"} label={s.enabled ? "Enabled" : "Disabled"} /></td>
                <td className="py-2 text-right">
                  <div className="inline-flex gap-1.5">
                    <button type="button" className={BTN} onClick={() => toggle.mutate({ id: s.id, enabled: !s.enabled })}>{s.enabled ? "Disable" : "Enable"}</button>
                    <button type="button" className={BTN} onClick={() => { if (confirm("Delete subscription?")) del.mutate(s.id); }}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Section>
  );
}

function DeliveriesSection() {
  const q = useQuery({ queryKey: ["notif-deliveries"], queryFn: () => dashApi.notificationDeliveries(30) });
  return (
    <Section title="Recent deliveries" desc="Append-only delivery history (read-only).">
      {q.isLoading ? <LoadingState /> : q.isError ? <ErrorState error={q.error} /> : !q.data?.deliveries.length ? (
        <EmptyState compact>No deliveries yet.</EmptyState>
      ) : (
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
            <th className="pb-2 font-medium">When</th><th className="pb-2 font-medium">Channel</th><th className="pb-2 font-medium">Target</th><th className="pb-2 font-medium">Subject</th><th className="pb-2 font-medium">Status</th>
          </tr></thead>
          <tbody className="divide-y divide-ink-500/40">
            {q.data.deliveries.map((d) => (
              <tr key={d.id} className="text-slate-300">
                <td className="py-2 text-[11px] text-slate-500">{fmtRelative(d.created_at)}</td>
                <td className="py-2 text-slate-400">{d.channel_type}</td>
                <td className="py-2 text-slate-400">{d.target}</td>
                <td className="py-2 truncate max-w-xs">{d.subject}</td>
                <td className="py-2"><StatusPill status={d.status === "delivered" ? "ok" : d.status === "failed" ? "down" : "no_data"} label={d.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Section>
  );
}

export default function ReportingAdminPage() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-lg font-semibold text-white">Reporting & Notifications</h1>
        <p className="text-sm text-slate-400">Manage report schedules, delivery channels and subscriptions. All changes are admin-only and audited.</p>
      </div>
      <SchedulesSection />
      <ChannelsSection />
      <SubscriptionsSection />
      <DeliveriesSection />
    </div>
  );
}
